import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface Blocker {
  state: 'unblocked' | 'blocked';
  proceed: () => void;
  reset: () => void;
}

const isExternal = (href: string): boolean =>
  href.startsWith('http://') ||
  href.startsWith('https://') ||
  href.startsWith('//') ||
  href.startsWith('mailto:') ||
  href.startsWith('tel:') ||
  href.startsWith('javascript:');

/**
 * Bloquea la navegación interna (clics en enlaces) cuando hay cambios sin
 * guardar. Funciona con BrowserRouter, a diferencia de useBlocker de
 * react-router-dom que requiere un data router.
 */
export function useBlocker(when: boolean): Blocker {
  const navigate = useNavigate();
  const location = useLocation();
  const whenRef = useRef(when);
  whenRef.current = when;

  const [blocked, setBlocked] = useState(false);
  const pendingRef = useRef<(() => void) | null>(null);

  const proceed = useCallback(() => {
    setBlocked(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.();
  }, []);

  const reset = useCallback(() => {
    setBlocked(false);
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    if (!when) return;

    const onClick = (e: MouseEvent) => {
      if (!whenRef.current) return;
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || href === '#' || isExternal(href)) return;

      // No bloquear si se hace clic en la página en la que ya estamos.
      if (href === location.pathname) return;

      e.preventDefault();
      pendingRef.current = () => navigate(href);
      setBlocked(true);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [when, navigate, location.pathname]);

  return { state: blocked ? 'blocked' : 'unblocked', proceed, reset };
}